<script lang="ts">
  import * as d3 from 'd3';
  import type { ChartQuantitative, QuantitativeItem } from '$lib/sanity/schemas';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import ChartLayout from './components/ChartLayout.svelte';
  import Legend from './components/Legend.svelte';
  import Table from './components/Table.svelte';
  import ToolTip, { toolTipStore } from './components/ToolTip.svelte';
  import { addDataLabels, setUpChart, type DataLabel } from './utils/chart';
  import { getPalette } from './utils/color';
  import {
    categorizeChartItems,
    convertToClass,
    formatDocumentId,
    formatGraphValue
  } from './utils/data';
  import {
    INTERACTION_CLASS,
    barInteraction,
    interactionStore,
    labelInteraction,
    labelBackgroundInteraction
  } from './utils/interactions';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: ChartQuantitative;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
    gridColspan: number;
  }

  let { document, sourceMap, path = [], gridColspan }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  const {
    _id,
    alt,
    chart = null,
    colorTheme = '',
    footnote = null,
    headline = '',
    legendDisplay = '',
    prefix = '',
    subhead = '',
    suffix = ''
  } = document;
  const { items = [] } = chart || {};
  const categories = categorizeChartItems(items);
  const id = formatDocumentId(_id);
  const store = interactionStore(id);
  const toolTip = toolTipStore();
  const colorPalette = getPalette(colorTheme, categories.secondary.length);
  const hasMultipleGroups = categories.secondary.length > 1;
  const tableProps = {
    header: hasMultipleGroups ? ['Category', 'Group', 'Value'] : ['Category', 'Value'],
    rows: items.map((item) => {
      let list = [item.primaryCategory, item.value];

      if (hasMultipleGroups) {
        list = [item.primaryCategory, item.secondaryCategory, item.value];
      }

      return list;
    })
  };
  const useToolTip = (x0: number, x1: number, y0: number, y1: number) => {
    const width = x1 - x0;
    const height = y1 - y0;

    return width < 120 && height > 20;
  };

  const renderChart = (chartElement: HTMLElement) => {
    const { selection, sizes } = setUpChart(chartElement, { alt, colorPalette });
    const root = d3
      .hierarchy({ children: items })
      .sum(function (d: any) {
        return d.value;
      })
      .sort((a, b) => {
        return (b.value || 0) - (a.value || 0);
      });
    const treemap = d3
      .treemap<{ children: QuantitativeItem[] }>()
      .tile(d3.treemapSquarify)
      .size([sizes.graph.width, sizes.graph.height])
      .padding(2);
    const tree = treemap(root);

    // Draw leaves
    selection
      .selectAll('.treemap-rect')
      .data(tree?.children || [])
      .enter()
      .append('rect')
      .attr('x', function (d) {
        return d.x0;
      })
      .attr('y', function (d) {
        return d.y0;
      })
      .attr('width', function (d) {
        return d.x1 - d.x0;
      })
      .attr('height', function (d) {
        return d.y1 - d.y0;
      })
      .attr('stroke', '#000000')
      .attr('fill', function (d: any) {
        const data = d.data as QuantitativeItem;
        const index = categories.secondary.indexOf(data.secondaryCategory);
        const color = colorPalette.colors[index];

        return color?.color || '#000000';
      })
      .each(function (d: any) {
        const data = d.data as QuantitativeItem;
        const classname = `${INTERACTION_CLASS} ${barInteraction.classname} ${convertToClass(data.primaryCategory)}`;
        const interactions = [barInteraction, labelInteraction, labelBackgroundInteraction];
        const selected = [convertToClass(data.primaryCategory)];

        d3.select(this)
          .style('cursor', 'pointer')
          .attr('class', classname)
          .on('mouseover', function () {
            store.triggerInteraction(interactions, selected);
          })
          .on('mouseout', function () {
            store.resetInteraction(interactions);

            if (useToolTip(d.x0, d.x1, d.y0, d.y1)) {
              toolTip.hide();
            }
          })
          .on('mousemove', function (event, d: any) {
            if (useToolTip(d.x0, d.x1, d.y0, d.y1)) {
              const data = d.data as QuantitativeItem;
              const markup = `
                <p class="category">${data.primaryCategory}</p>
                <p>${formatGraphValue(data.value, prefix, suffix)}</p>
              `;

              toolTip.show(event.clientX, event.clientY, markup);
            }
          });
      });

    // Draw data labels
    const offsetX = 20;
    const offsetY = 30;
    const offsetY2 = 75;

    const labelItems = (tree?.children || [])?.filter((d: any) => {
      return !useToolTip(d.x0, d.x1, d.y0, d.y1);
    });

    const categoryLabels: DataLabel[] = labelItems.map(function (d: any): DataLabel {
      const data = d.data as QuantitativeItem;
      const selected = [convertToClass(data.primaryCategory)];

      return {
        color: '#ffffff',
        backgroundColor: '#000000',
        offsetPadding: true,
        selected,
        text: data.primaryCategory,
        textAnchor: 'start',
        x: d.x0 + offsetX,
        y: d.y0 + offsetY
      };
    });

    addDataLabels(selection, categoryLabels, store);

    const valueLabels: DataLabel[] = labelItems.map(function (d: any): DataLabel {
      const data = d.data as QuantitativeItem;
      const selected = [convertToClass(data.primaryCategory)];

      return {
        hidden: true,
        offsetPadding: true,
        selected,
        selectedColor: '#ffffff',
        selectedBackgroundColor: '#000000',
        text: formatGraphValue(data.value, prefix, suffix),
        textAnchor: 'start',
        x: d.x0 + offsetX,
        y: d.y0 + offsetY2
      };
    });

    addDataLabels(selection, valueLabels, store);
  };
</script>

<ToolTip />
<ChartLayout
  {footnote}
  {headline}
  {id}
  {legendDisplay}
  {subhead}
  {renderChart}
  {path}
  {encodeDataAttribute}
  {gridColspan}
>
  {#snippet legend()}
    <Legend
      categories={categories.secondary}
      {colorPalette}
      interactions={[barInteraction, labelInteraction]}
      {legendDisplay}
      {store}
      {gridColspan}
    />
  {/snippet}
  {#snippet table()}
    <Table {...tableProps} {gridColspan} />
  {/snippet}
</ChartLayout>
